/**
 * appLinks — single source of truth for the store links + a fire-and-forget
 * download beacon. Both apps are now LIVE on the public stores:
 * Android on Google Play, iOS on the App Store.
 *
 * Store links come from Vite env (VITE_ANDROID_PLAY_URL / VITE_IOS_APPSTORE_URL)
 * with the live production values baked in as fallbacks, so the site works even
 * if the env isn't wired at build time.
 *
 * trackDownload() posts to the backend download-funnel beacon
 * (POST /v2/app/download-event) using navigator.sendBeacon with a text/plain
 * blob — a CORS "simple request", so it needs NO preflight and the marketing
 * origin need not be in the API's ALLOWED_ORIGINS. Best-effort; never throws.
 */
const env = (import.meta as unknown as { env?: Record<string, string> }).env || {}

export const ANDROID_URL =
  env.VITE_ANDROID_PLAY_URL ||
  'https://play.google.com/store/apps/details?id=com.bimauncle.calculator'

/** iOS is LIVE on the App Store (approved 2026-07-12). */
export const IOS_APPSTORE_URL =
  env.VITE_IOS_APPSTORE_URL ||
  'https://apps.apple.com/in/app/bimauncle-premium-calculator/id6772143703'

/** Legacy TestFlight invite — kept for the early-access "Become a Tester"
 *  flow; the public download CTAs now use IOS_APPSTORE_URL. */
export const IOS_TESTFLIGHT_URL =
  env.VITE_IOS_TESTFLIGHT_URL || 'https://testflight.apple.com/join/UMEJbhUC'

/** Branded smart-redirect that routes each visitor to the right store. */
export const GET_URL = 'https://bimauncle.com/get'

const API_BASE = env.VITE_API_BASE || 'https://api.bimauncle.com'

export type DevicePlatform = 'android' | 'ios' | 'desktop'

export function detectPlatform(): DevicePlatform {
  if (typeof navigator === 'undefined') return 'desktop'
  const ua = navigator.userAgent || (navigator as { vendor?: string }).vendor || ''
  if (/android/i.test(ua)) return 'android'
  const iOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  if (iOS) return 'ios'
  return 'desktop'
}

export type DownloadAction = 'view' | 'android' | 'ios' | 'desktop'

/** Fire-and-forget download-funnel beacon. Safe to call from click handlers. */
export function trackDownload(action: DownloadAction): void {
  try {
    const payload = JSON.stringify({
      source: 'marketing',
      action,
      platform: detectPlatform(),
    })
    const url = `${API_BASE}/v2/app/download-event`
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([payload], { type: 'text/plain;charset=UTF-8' })
      navigator.sendBeacon(url, blob)
      return
    }
    // Fallback: no-cors keepalive POST (also preflight-free with text/plain).
    void fetch(url, {
      method: 'POST',
      mode: 'no-cors',
      keepalive: true,
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: payload,
    }).catch(() => undefined)
  } catch {
    /* analytics must never break the page */
  }
}
